import { AppLayout as CloudscapeLayout, SideNavigation, BreadcrumbGroup, Header, Box, SpaceBetween } from '@cloudscape-design/components'
import { useNavigate, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { usePageHeader } from '../../providers/PageHeaderProvider'
import { useAmplifyAuth } from '../../providers/AmplifyAuthProvider'
import { signOut } from 'aws-amplify/auth'

interface AppLayoutProps {
  children: React.ReactNode
  user: any
  signOut: () => void
}

function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [navigationOpen, setNavigationOpen] = useState(true)
  const { headerConfig } = usePageHeader()
  const { user } = useAmplifyAuth()

  const handleSignOut = async () => {
    try {
      localStorage.removeItem('alterra_session')
      localStorage.clear()
      await signOut({ global: true })
      window.location.href = '/'
    } catch (error) {
      console.error('Error signing out:', error)
      window.location.href = '/'
    }
  }

  const getNavItems = (): Array<{type: string, text?: string, href?: string}> => {
    const baseItems = [
      { type: 'link', text: 'Home', href: '/' },
      { type: 'link', text: 'Assets', href: '/assets' },
      { type: 'link', text: 'Dashboards', href: '/dashboards' },
    ]

    if (user?.role === 'admin' || user?.role === 'manager') {
      baseItems.push(
        { type: 'divider' },
        { type: 'link', text: 'Admin', href: '/admin' }
      )
    }

    return baseItems
  }

  // Custom navigation component with branding and profile
  const CustomNavigation = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Branding Section */}
      <div style={{ 
        padding: '16px',
        borderBottom: '1px solid #e9ebed',
        cursor: 'pointer'
      }} onClick={() => navigate('/')}>
        <div style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          color: '#0973ba',
          lineHeight: '1.2'
        }}>
          Alterra SmartFactory
        </div>
      </div>

      {/* Navigation Links */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <SideNavigation
          activeHref={location.pathname}
          items={getNavItems()}
          onFollow={event => {
            if (!event.detail.external) {
              event.preventDefault()
              navigate(event.detail.href)
            }
          }}
        />
      </div>

      {/* Profile Section */}
      <div style={{ 
        borderTop: '1px solid #e9ebed',
        padding: '12px 16px'
      }}>
        <SpaceBetween size="xs">
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#232f3e' }}>
            {user?.name || 'User'}
          </div>
          <div style={{ fontSize: '12px', color: '#5f6b7a' }}>
            {user?.email}
          </div>
          <div style={{ fontSize: '11px', color: '#879596', textTransform: 'capitalize' }}>
            {user?.role} • {user?.department || 'General'}
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: '1px solid #f2f3f3'
          }}>
            <button
              onClick={() => navigate('/profile')}
              style={{
                background: 'none',
                border: 'none',
                color: '#0973ba',
                fontSize: '12px',
                cursor: 'pointer',
                padding: '0'
              }}
            >
              Profile
            </button>
            <span style={{ color: '#e9ebed' }}>•</span>
            <button
              onClick={() => navigate('/settings')}
              style={{
                background: 'none',
                border: 'none',
                color: '#0973ba',
                fontSize: '12px',
                cursor: 'pointer',
                padding: '0'
              }}
            >
              Settings
            </button>
            <span style={{ color: '#e9ebed' }}>•</span>
            <button
              onClick={handleSignOut}
              style={{
                background: 'none',
                border: 'none',
                color: '#d91515',
                fontSize: '12px',
                cursor: 'pointer',
                padding: '0'
              }}
            >
              Sign Out
            </button>
          </div>
        </SpaceBetween>
      </div>
    </div>
  )

  return (
    <CloudscapeLayout
      navigationOpen={navigationOpen}
      onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
      navigationWidth={250}
      navigation={<CustomNavigation />}
      breadcrumbs={
        <BreadcrumbGroup 
          items={(() => {
            const items = [{ text: 'Home', href: '/' }]
            
            // Add section-specific breadcrumbs
            if (location.pathname.startsWith('/dashboards')) {
              items.push({ text: 'Dashboards', href: '/dashboards' })
              
              // Handle dashboard routes
              if (location.pathname !== '/dashboards') {
                const pathParts = location.pathname.split('/')
                const dashboardId = pathParts[2] // /dashboards/{id} or /dashboards/{id}/edit
                
                if (location.pathname.includes('/edit')) {
                  // Edit mode: Home > Dashboards > Dashboard Name > Edit
                  const dashboardViewPath = `/dashboards/${dashboardId}`
                  const dashboardName = headerConfig.title || 'Dashboard'
                  
                  items.push({ text: dashboardName, href: dashboardViewPath })
                  items.push({ text: 'Edit', href: location.pathname })
                } else if (headerConfig.title) {
                  // View mode: Home > Dashboards > Dashboard Name
                  items.push({ text: headerConfig.title, href: location.pathname })
                } else if (dashboardId) {
                  // Fallback if no title is set
                  items.push({ text: 'Dashboard', href: location.pathname })
                }
              }
            } else if (location.pathname.startsWith('/assets')) {
              items.push({ text: 'Assets', href: '/assets' })
              if (headerConfig.title && location.pathname !== '/assets') {
                items.push({ text: headerConfig.title, href: location.pathname })
              }
            } else if (location.pathname.startsWith('/admin')) {
              items.push({ text: 'Admin', href: '/admin' })
              if (headerConfig.title && location.pathname !== '/admin') {
                items.push({ text: headerConfig.title, href: location.pathname })
              }
            } else if (headerConfig.title) {
              items.push({ text: headerConfig.title, href: location.pathname })
            }
            
            return items
          })()}
          expandAriaLabel="Show path" 
          ariaLabel="Breadcrumbs"
          onFollow={event => {
            if (!event.detail.external) {
              event.preventDefault()
              navigate(event.detail.href)
            }
          }}
        />
      }
      content={
        <>
          {headerConfig.title && (
            <Header
              variant="h1"
              description={headerConfig.description}
              actions={
                headerConfig.actions && headerConfig.actions.length > 0 ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {headerConfig.actions.map(action => (
                      <div key={action.id}>{action.component}</div>
                    ))}
                  </div>
                ) : undefined
              }
            >
              {headerConfig.title}
            </Header>
          )}
          <div style={{ padding: '16px', height: '100%', overflow: 'auto' }}>
            {children}
          </div>
        </>
      }
      headerSelector="#header"
      toolsHide
      contentType="default"
      navigationHide={false}
    />
  )
}

export default AppLayout